import { useState } from 'react'
import { useSummaryStaleness } from '../hooks/useSummaryStaleness.js'
import { timeAgo } from '../data/metaKeys.js'

/**
 * StalenessBar — amber bar that appears above the exec summary body
 * when any input has a newer savedAt/createdAt/at than the exec
 * summary's generatedAt.
 *
 * Collapsed by default: single line with count + Regenerate button.
 * Clicking the bar (not the button) expands a list of what changed.
 *
 * Renders nothing when the summary is fresh or doesn't exist — kept
 * inside the exec summary card so layout spacing only reserves room
 * when the bar is actually there.
 *
 * Props:
 *   onRegenerate — () => void, fired when the Regenerate button is
 *                  clicked. Wires to the same handler the exec card's
 *                  "Regenerate" button uses, so the two entry points
 *                  produce identical behaviour (loading state, error
 *                  handling, preview flow).
 *   regenerating — boolean, whether a regenerate is currently in
 *                  flight — disables the button to prevent double-fire.
 */
export default function StalenessBar({ onRegenerate, regenerating }) {
  const { isStale, staleCount, changes } = useSummaryStaleness()
  const [expanded, setExpanded] = useState(false)

  if (!isStale) return null

  const plural = staleCount === 1 ? 'update' : 'updates'

  return (
    <div style={{ borderBottom: '0.5px solid #EF9F27' }}>
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: '#FAEEDA',
          border: 'none',
          borderBottom: expanded ? '0.5px solid #EF9F27' : 'none',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#BA7517',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {staleCount}
        </span>
        <span style={{ fontSize: 12, color: '#633806', lineHeight: 1.5, flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 700 }}>
            {staleCount} {plural} since this was generated.
          </span>{' '}
          <span style={{ color: '#854F0B' }}>
            {expanded ? 'Hide details' : 'Show details'}
          </span>
        </span>
        <span style={{ fontSize: 11, color: '#854F0B' }}>{expanded ? '▾' : '▸'}</span>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation()
            if (!regenerating) onRegenerate?.()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              if (!regenerating) onRegenerate?.()
            }
          }}
          style={{
            display: 'inline-block',
            background: regenerating ? '#FAC775' : '#fff',
            border: '0.5px solid #EF9F27',
            color: regenerating ? '#854F0B' : '#633806',
            padding: '4px 12px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 700,
            cursor: regenerating ? 'wait' : 'pointer',
            userSelect: 'none',
          }}
        >
          {regenerating ? 'Regenerating…' : 'Regenerate'}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '10px 16px 12px', background: '#FFFBF0' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {changes.map((c, i) => {
              const pillStyle = pillFor(c.type)
              return (
                <li key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      color: pillStyle.color,
                      background: pillStyle.bg,
                      padding: '1px 7px',
                      borderRadius: 99,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.type}
                  </span>
                  <span
                    style={{ fontSize: 12, color: 'var(--text)', flex: 1, minWidth: 0 }}
                  >
                    {c.label}
                    {c.author ? (
                      <>
                        {' '}
                        — <span style={{ color: 'var(--muted)' }}>by {c.author}</span>
                      </>
                    ) : null}
                    {c.detail ? (
                      <>
                        {' '}
                        <span style={{ color: 'var(--muted)' }}>({c.detail})</span>
                      </>
                    ) : null}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--muted-light)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.at ? timeAgo(new Date(c.at).getTime()) : ''}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Color palette for change type pills, aligned with the summary
 * bullet pill colors so the staleness list feels like part of the
 * same visual system.
 */
function pillFor(type) {
  if (type === 'Goal' || type === 'Subgoal') return { color: '#185FA5', bg: '#E6F1FB' }
  if (type === 'Team') return { color: '#0F6E56', bg: '#E1F5EE' }
  return { color: '#5F5E5A', bg: '#F1EFE8' }
}
